import { StateService, TargetState, UIRouterModule } from "@uirouter/angular";
import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { combineLatest, mergeMap, of } from "rxjs";
import { CurrentEmployeeActions, ICurrentEmployeeStoreState } from "@gtmhub/employees";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { getCurrentUserId } from "@gtmhub/users";
import { AnalyticsModule } from "@webapp/analytics/analytics.module";
import { TrackingMetadata } from "@webapp/analytics/models/analytics.model";
import { ReduxStoreObserver } from "@webapp/core/state-management/redux-store-observer";
import { LocalizationModule } from "@webapp/localization/localization.module";
import { NavigationReportsCacheService } from "@webapp/navigation/services/navigation-reports-cache.service";
import { MetricProgressBarComponent } from "@webapp/okrs/metrics/components/metric-progress-bar/metric-progress-bar.component";
import { SessionsRepository } from "@webapp/sessions/services/sessions-repository.service";
import { UiIconModule } from "@webapp/ui/icon/icon.module";
import { UiLoadingIndicatorModule } from "@webapp/ui/loading-indicator/loading-indicator.module";
import { UiProgressModule } from "@webapp/ui/progress/progress.module";
import { UiSkeletonModule } from "@webapp/ui/skeleton/skeleton.module";
import { UiTooltipModule } from "@webapp/ui/tooltip/tooltip.module";

interface SummaryReportView {
  my: {
    attainment: number;
    attainmentDelta: number;
  };
  company: {
    attainment: number;
    attainmentDelta: number;
  };
  teams?: {
    attainment: number;
    attainmentDelta: number;
  };
}

@Component({
  selector: "session-summary-report",
  templateUrl: "./session-summary-report.component.html",
  styleUrls: ["./session-summary-report.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    LocalizationModule,
    UIRouterModule,
    UiProgressModule,
    MetricProgressBarComponent,
    UiIconModule,
    AnalyticsModule,
    UiSkeletonModule,
    UiLoadingIndicatorModule,
    UiTooltipModule,
  ],
})
export class SessionSummaryReportComponent implements OnChanges {
  @Input()
  public summaryReport: SummaryReportView;
  @Input()
  public expanded = false;
  @Input()
  public sessionId: string;
  @Input() public trackMeta: TrackingMetadata;
  @Output()
  public readonly expandedChange = new EventEmitter<boolean>();

  public reduxObserver: ReduxStoreObserver;
  public get reportState(): TargetState | null {
    const performanceReport = this.navigationReportsCache.get().find((report) => report.name.toLowerCase() === "performance report");
    if (!performanceReport) {
      return null;
    }

    return this.stateService.target("gtmhub.insightboard", { dashboardId: performanceReport.id, sessionId: this.sessionId });
  }

  constructor(
    private stateService: StateService,
    private sessionsRepository: SessionsRepository,
    private navigationReportsCache: NavigationReportsCacheService,
    private currentEmployeeActions: CurrentEmployeeActions,
    private cdr: ChangeDetectorRef
  ) {
    const { reduxStore } = reduxStoreContainer;
    this.reduxObserver = new ReduxStoreObserver(reduxStore);
    reduxStore.dispatch(this.currentEmployeeActions.fetchCurrentEmployeeIfMissing());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.expanded && changes.expanded.currentValue === true && this.sessionId && !this.summaryReport) {
      this.getSessionsProgress(this.sessionId);
    }
  }

  public toggleExpanded(): void {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  public getTrackingMetadata(): TrackingMetadata {
    return {
      button_name: "my_okrs_graph",
      expanded: this.expanded,
      session_id: this.sessionId,
      user_id: getCurrentUserId(),
      ...this.trackMeta,
    };
  }

  private getSessionsProgress(sessionId: string): void {
    this.reduxObserver
      .whenFetched$<ICurrentEmployeeStoreState>("currentEmployee")
      .pipe(
        mergeMap((state) => {
          const currentEmployee = state.currentEmployee.info;
          const assigneeIdsToLoad = [...new Set([currentEmployee.id, ...currentEmployee.teamIds, ...currentEmployee.managedTeamIds])].join(",");
          return combineLatest([of(currentEmployee), this.sessionsRepository.getSessionsProgress$(sessionId, assigneeIdsToLoad)]);
        })
      )
      .subscribe(([profile, stats]) => {
        if (stats.items.length) {
          const sessionProgress = stats.items[0];

          const myProgressStats = sessionProgress.assigneesStats?.find((item) => item.assigneeId === getCurrentUserId());
          const myProgress = {
            attainment: myProgressStats?.progress || 0,
            attainmentDelta: myProgressStats?.attainmentDelta.delta || 0,
          };

          const companyProgress = {
            attainment: sessionProgress.sessionProgress,
            attainmentDelta: sessionProgress.sessionAttainmentDelta.delta,
          };

          this.summaryReport = {
            my: myProgress,
            company: companyProgress,
          };

          const teamsProgressStats = sessionProgress.assigneesStats?.filter(
            (item) => profile.teamIds.includes(item.assigneeId) || profile.managedTeamIds.includes(item.assigneeId)
          );

          if (teamsProgressStats?.length) {
            const teamsProgress = {
              attainment: teamsProgressStats.map((stat) => stat.progress).reduce((acc, item) => acc + item, 0) / teamsProgressStats.length,
              attainmentDelta: teamsProgressStats.map((stat) => stat.attainmentDelta.delta).reduce((acc, item) => acc + item, 0) / teamsProgressStats.length,
            };
            this.summaryReport.teams = teamsProgress;
          }
        }

        this.cdr.detectChanges();
      });
  }
}
